import { supabase } from '@/integrations/supabase/client'

/**
 * Web Push for this browser: the notifications that reach a phone or computer
 * even when the site is closed. No app store is involved. The browser vendor
 * runs the push service, public/sw.js shows what arrives, and push-send
 * (supabase/functions/push-send) sends it.
 *
 * A device is one row in push_subscriptions. Turning push on here registers
 * the service worker, asks the browser's permission and saves the device;
 * which kinds of push it then gets is the person's notification_preferences,
 * shared by all their devices.
 */

export type PushAvailability =
  /** Ready to be switched on or off. */
  | 'available'
  /** An iPhone or iPad in a Safari tab: Apple allows push only to a site
   *  added to the home screen and opened from there. */
  | 'needs-install'
  /** The person said no; only the browser's own site settings can undo it. */
  | 'blocked'
  /** Lovable's editor preview, which must not register a worker (see below). */
  | 'preview'
  /** Another app's built-in browser (Instagram, Facebook…), which cannot show
   *  push on any platform; see inAppBrowser(). */
  | 'in-app'
  /** A browser with no Web Push at all. */
  | 'unsupported'

function isIos(): boolean {
  const ua = navigator.userAgent
  // iPadOS reports itself as a Mac; the touch screen gives it away.
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1)
}

/**
 * A phone or tablet, for settings that only make sense on a computer (the
 * chime). Chromium says so directly; elsewhere the user agent does, and
 * Android tablets, which drop "Mobi", count as mobile too. A phone set to
 * "Request desktop site" passes as a computer, which is harmless here.
 */
export function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false
  const hint = (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData?.mobile
  if (hint) return true
  return /Android|Mobi|iPhone|iPad|iPod/.test(navigator.userAgent) || isIos()
}

function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

/**
 * Lovable's editor shows the app in an iframe on its preview hosts. A service
 * worker registered there would outlive the preview and keep claiming those
 * pages, so push is simply not offered in the editor.
 */
function isPreview(): boolean {
  if (window.self !== window.top) return true
  const host = window.location.hostname
  return host.endsWith('.lovableproject.com') || host.startsWith('id-preview--')
}

const IN_APP: [RegExp, string][] = [
  [/Instagram/, 'Instagram'],
  // Messenger shares Facebook's tokens and adds its own name.
  [/(FBAN|FBAV|FB_IAB).*(Messenger|MESSENGER)|(Messenger|MESSENGER).*(FBAN|FBAV|FB_IAB)/, 'Messenger'],
  [/FBAN|FBAV|FB_IAB/, 'Facebook'],
  [/musical_ly|BytedanceWebview|TikTok/, 'TikTok'],
  [/LinkedInApp/, 'LinkedIn'],
  [/Snapchat/, 'Snapchat'],
  [/ Line\//, 'Line'],
]

/**
 * The app whose built-in browser this is, null for one that does not name
 * itself, undefined for a real browser. Only Android marks an unnamed one
 * ("; wv)"); an unnamed iOS web view is not guessed at, because Chrome and
 * Firefox on iOS are web views too and would be caught with it.
 */
export function inAppBrowser(): string | null | undefined {
  if (typeof navigator === 'undefined') return undefined
  const ua = navigator.userAgent
  for (const [pattern, name] of IN_APP) if (pattern.test(ua)) return name
  return /Android.*; wv\)/.test(ua) ? null : undefined
}

export function pushAvailability(): PushAvailability {
  if (typeof window === 'undefined') return 'unsupported'
  if (isPreview()) return 'preview'
  // Before the iOS check: an app's browser on an iPhone cannot add the site
  // to the home screen, so those steps would be impossible to follow.
  if (inAppBrowser() !== undefined) return 'in-app'
  const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
  if (!supported) return isIos() && !isStandalone() ? 'needs-install' : 'unsupported'
  if (Notification.permission === 'denied') return 'blocked'
  return 'available'
}

/** True on iOS when the site is not yet on the home screen, for the how-to. */
export function isIosBrowserTab(): boolean {
  return typeof window !== 'undefined' && isIos() && !isStandalone()
}

/**
 * Whether this person already gets push in the home-screen app on an iPhone
 * or iPad, asked from a Safari tab. The tab and the home-screen app keep
 * separate storage, so the tab cannot see the app's subscription and would
 * otherwise tell someone whose notifications work to set them up. Their saved
 * device (push_subscriptions) is the only evidence it can reach.
 *
 * Matched on the device family, not the exact device: someone with two
 * iPhones is taken to have both set up. An iPad's home-screen app reports
 * itself as a Mac, so on an iPad any saved Mac counts too. Both err towards
 * not nagging; Settings still offers the steps.
 */
export async function hasIosDeviceOnFile(): Promise<boolean> {
  if (!isIosBrowserTab()) return false
  const family = /iPhone|iPod/.test(navigator.userAgent) ? ['%iPhone%'] : ['%iPad%', '%Macintosh%']
  const { data, error } = await supabase
    .from('push_subscriptions')
    .select('id')
    .or(family.map(f => `user_agent.ilike.${f}`).join(','))
    .limit(1)
  // Unknown counts as no: the offer is the safe fallback, not silence.
  return !error && (data?.length ?? 0) > 0
}

function base64UrlToBytes(value: string): Uint8Array {
  const padded = (value + '='.repeat((4 - (value.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(padded)
  return Uint8Array.from(raw, c => c.charCodeAt(0))
}

function sameKey(a: ArrayBuffer | null | undefined, b: Uint8Array): boolean {
  if (!a) return false
  const x = new Uint8Array(a)
  return x.length === b.length && x.every((v, i) => v === b[i])
}

/**
 * Whether this browser is known to be receiving pushes, so lib/chatNotify can
 * skip its own pop-up while the tab is hidden: the push shows the same message
 * a moment later, and two pop-ups for one message is the bug that gets
 * notifications turned off. Kept in step by the functions below.
 */
let activeHere = false

export function isPushActiveHere(): boolean {
  return activeHere
}

let publicKey: Promise<string> | null = null

/** The VAPID public key every subscription is made against. */
function vapidPublicKey(): Promise<string> {
  publicKey ??= supabase.functions
    .invoke('push-send', { body: { action: 'public-key' } })
    .then(({ data, error }) => {
      if (error || typeof data?.publicKey !== 'string') throw error ?? new Error('No push key')
      return data.publicKey as string
    })
    .catch(err => {
      publicKey = null
      throw err
    })
  return publicKey
}

async function existingRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  if (!('serviceWorker' in navigator)) return undefined
  return navigator.serviceWorker.getRegistration('/')
}

async function save(sub: PushSubscription) {
  const json = sub.toJSON()
  const { error } = await supabase.rpc('save_push_subscription', {
    _endpoint: sub.endpoint,
    _p256dh: json.keys?.p256dh ?? '',
    _auth: json.keys?.auth ?? '',
    _user_agent: navigator.userAgent,
  })
  if (error) throw error
}

/** Whether this browser is currently set up to receive pushes. */
export async function isThisDeviceSubscribed(): Promise<boolean> {
  if (pushAvailability() !== 'available' || Notification.permission !== 'granted') return false
  const reg = await existingRegistration()
  return !!(await reg?.pushManager.getSubscription())
}

/**
 * Turn push on for this browser. Must run from a tap: browsers only show the
 * permission prompt in response to one (iOS refuses it otherwise).
 * Resolves to false when the person declined.
 */
export async function enablePush(): Promise<boolean> {
  if (pushAvailability() !== 'available') return false
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return false

  const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' })
  await navigator.serviceWorker.ready
  const key = base64UrlToBytes(await vapidPublicKey())

  let sub = await reg.pushManager.getSubscription()
  // A subscription made against another key (an older deploy) would be
  // rejected by the push service on every send.
  if (sub && !sameKey(sub.options.applicationServerKey, key)) {
    await sub.unsubscribe()
    sub = null
  }
  sub ??= await reg.pushManager.subscribe({
    userVisibleOnly: true,
    // slice() detaches the view from any shared/larger buffer; the cast is for
    // the newer TS lib types, which no longer accept Uint8Array here directly.
    applicationServerKey: key.slice().buffer as ArrayBuffer,
  })
  await save(sub)
  activeHere = true
  return true
}

/** Turn push off for this browser only. The person's other devices keep it. */
export async function disablePush(): Promise<void> {
  activeHere = false
  const reg = await existingRegistration()
  const sub = await reg?.pushManager.getSubscription()
  if (!sub) return
  await supabase.from('push_subscriptions').delete().eq('endpoint', sub.endpoint)
  await sub.unsubscribe()
}

/**
 * Called on sign-out, while the session still exists to delete the row with.
 * A shared computer must not keep receiving the previous person's messages.
 * Never throws: signing out must work even when this cannot.
 */
export async function forgetThisDevice(): Promise<void> {
  try {
    // Capped, so a dead connection never holds up signing out.
    await Promise.race([disablePush(), new Promise(resolve => setTimeout(resolve, 3000))])
  } catch {
    // Offline, or the row is already gone. The next person to enable push on
    // this browser takes the endpoint over anyway (save_push_subscription).
  }
}

/**
 * Re-save this browser's subscription for whoever is signed in now. Push
 * services rotate endpoints, and a shared computer changes hands; the row
 * follows. Does nothing unless push was already on here.
 */
export async function resyncPush(): Promise<void> {
  try {
    activeHere = await isThisDeviceSubscribed()
    if (!activeHere) return
    const reg = await existingRegistration()
    const sub = await reg?.pushManager.getSubscription()
    if (sub) await save(sub)
  } catch {
    // A best-effort refresh; the settings page shows the real state.
  }
}
